/**
 * Super Admin editor for project_templates rows whose agent_id is null.
 * Homeowner names and contact details never belong in this payload.
 */

const PRIVATE_KEY = /email|phone|contact|address|first_name|last_name|homeowner/i;
const KNOWN_TASK_KEYS = new Set([
  'id',
  'title',
  'name',
  'description',
  'category',
  'frequency',
  'season',
  'notes',
  'completed',
]);

function cleanText(value) {
  return String(value == null ? '' : value).trim();
}

function newTaskId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `draft-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function taskFromRow(task, index) {
  const row = task && typeof task === 'object' ? task : {};
  const extra = {};
  Object.keys(row).forEach((key) => {
    if (KNOWN_TASK_KEYS.has(key) || PRIVATE_KEY.test(key)) return;
    extra[key] = row[key];
  });
  const id = row.id != null && String(row.id) !== '' ? row.id : `draft-${index}`;
  return {
    id,
    title: String(row.title || row.name || ''),
    description: String(row.description || ''),
    category: String(row.category || ''),
    frequency: String(row.frequency || row.season || ''),
    notes: row.notes == null ? '' : String(row.notes),
    completed: row.completed === true,
    extra,
  };
}

function blankTask() {
  return taskFromRow({ id: newTaskId() }, 0);
}

function templateToForm(template) {
  const row = template || {};
  const tasks = Array.isArray(row.tasks) ? row.tasks : [];
  return {
    name: String(row.name || ''),
    description: String(row.description || ''),
    category: String(row.category || ''),
    frequency: String(row.frequency || ''),
    isHomeownerOnboarding: row.is_homeowner_onboarding === true || row.isHomeownerOnboarding === true,
    tasks: tasks.map((task, index) => taskFromRow(task, index)),
  };
}

function taskToJson(task) {
  const source = task || {};
  const out = { ...(source.extra || {}) };
  Object.keys(out).forEach((key) => {
    if (PRIVATE_KEY.test(key)) delete out[key];
  });
  out.id = source.id;
  out.title = cleanText(source.title);
  const description = cleanText(source.description);
  const category = cleanText(source.category);
  const frequency = cleanText(source.frequency);
  const notes = cleanText(source.notes);
  if (description) out.description = description;
  if (category) out.category = category;
  if (frequency) out.frequency = frequency;
  if (notes) out.notes = notes;
  if (source.completed === true) out.completed = true;
  return out;
}

function taskError(task) {
  if (!cleanText(task && task.title)) return 'Each task needs a name';
  if (!cleanText(task && task.category)) return 'Each task needs a category';
  if (!cleanText(task && task.frequency)) return 'Each task needs a frequency or season';
  return '';
}

function templateError(form) {
  const source = form || {};
  if (!cleanText(source.name)) return 'Add a name';
  if (!cleanText(source.category)) return 'Choose a category';
  if (!cleanText(source.frequency)) return 'Choose a frequency or season';
  const tasks = Array.isArray(source.tasks) ? source.tasks : [];
  if (!tasks.length) return 'Add at least one task';
  for (let index = 0; index < tasks.length; index += 1) {
    const problem = taskError(tasks[index]);
    if (problem) return problem;
  }
  return '';
}

function formToTemplateRecord(form) {
  const problem = templateError(form);
  if (problem) return { error: problem };
  const source = form || {};
  return {
    record: {
      name: cleanText(source.name),
      description: cleanText(source.description),
      category: cleanText(source.category),
      frequency: cleanText(source.frequency),
      tasks: source.tasks.map(taskToJson),
      agent_id: null,
      is_global: true,
      is_homeowner_onboarding: source.isHomeownerOnboarding === true,
    },
  };
}

function moveTask(tasks, index, direction) {
  const list = Array.isArray(tasks) ? tasks.slice() : [];
  const target = index + direction;
  if (index < 0 || index >= list.length || target < 0 || target >= list.length) return list;
  const [item] = list.splice(index, 1);
  list.splice(target, 0, item);
  return list;
}

function deleteTemplateCopy(name) {
  const label = cleanText(name) || 'This template';
  return {
    title: 'Delete this template?',
    message: `${label} will leave the starter list. Checklists an agent already copied stay as they are. This cannot be undone.`,
    confirmLabel: 'Delete',
  };
}

export {
  blankTask,
  deleteTemplateCopy,
  formToTemplateRecord,
  moveTask,
  taskError,
  taskFromRow,
  templateError,
  templateToForm,
};
